import React from "react";
import { Link } from "react-router-dom";
import { CommonBanner, Footer, Header } from "../../components";
import { C, FONT } from "../../constants/mq";

// Our History.
//
// Rebuilt from the group history. The old page led with a stock photograph
// captioned as the co-founder, which was not a picture of him — dropped
// rather than replaced, since we have no real portrait to use.

const milestones = [
  {
    year: "2002",
    title: "The first campus opens",
    body: "Testimony and Phoebe Kim open a coaching college in Castle Hill, preparing students for OC and Selective School exams. Lesson books are written and published in-house from the start.",
  },
  {
    year: "2003",
    title: "Growing past 400 students",
    body: "A partnership with Cornerstone Bookshop puts our material in front of more families, and enrolments pass four hundred students.",
  },
  {
    year: "2006",
    title: "Blended learning",
    body: "Classroom teaching is combined with online study so a student's work continues between lessons rather than stopping at the door.",
  },
  {
    year: "2007",
    title: "Research and development",
    body: "Coaching, publishing and curriculum development are brought together into one department, so what is taught and what is printed stay in step.",
  },
  {
    year: "2014",
    title: "Our own marking system",
    body: "An in-house OMR marking system is introduced, turning test results around quickly enough to act on them in the same term.",
  },
  {
    year: "2015",
    title: "Learning Management System",
    body: "Coursework, homework and assessment move onto a single platform that students, parents and teachers all work from.",
  },
  {
    year: "2016",
    title: "Connected School and Writing School",
    body: "Connected School launches alongside the Writing School program, extending structured teaching beyond the campus timetable.",
  },
  {
    year: "2018 – 2022",
    title: "Beyond New South Wales",
    body: "Campuses open in Victoria (2018), Queensland (2021) and South Australia (2022), with further Victorian locations in 2022. The group passes thirty branches.",
  },
];

// White sections under the banner, text in the same 1072 column as the
// banner heading (held to a readable measure), and a sand box to close.
const s = {
  intro: { background: "#ffffff", padding: "64px 0 8px" },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  narrow: { maxWidth: 820 },
  lead: {
    fontSize: 19,
    lineHeight: 1.7,
    color: C.body,
    margin: "0 0 20px",
    fontWeight: 500,
  },
  para: { fontSize: 15.5, lineHeight: 1.8, color: C.body, margin: "0 0 16px" },
  quote: {
    borderLeft: `3px solid ${C.bright}`,
    padding: "6px 0 6px 20px",
    margin: "32px 0 8px",
    fontSize: 17,
    lineHeight: 1.7,
    color: C.ink,
    fontStyle: "italic",
  },
  quoteBy: { fontSize: 13, color: C.muted, margin: "10px 0 0", fontStyle: "normal" },

  timelineWrap: { background: "#ffffff", padding: "48px 0 28px" },
  h2: {
    fontSize: 26,
    fontWeight: 700,
    color: C.ink,
    margin: "0 0 32px",
    letterSpacing: "-0.3px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: 24,
    paddingBottom: 28,
    marginBottom: 28,
    borderBottom: `1px solid ${C.line}`,
  },
  year: {
    fontSize: 15,
    fontWeight: 800,
    color: C.red,
    letterSpacing: "0.2px",
    paddingTop: 2,
  },
  itemTitle: { fontSize: 17, fontWeight: 700, color: C.ink, margin: "0 0 6px" },
  itemBody: { fontSize: 14.5, lineHeight: 1.75, color: C.body, margin: 0 },

  ctaBand: { background: "#ffffff", padding: "0 0 80px" },
  ctaBox: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    padding: 26,
  },
  ctaTitle: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px", lineHeight: 1.4 },
  ctaBody: { fontSize: 13.5, color: C.muted, margin: 0, lineHeight: 1.55 },
};

const responsiveCss = `
@media (max-width: 560px) {
  .cs-history-row { grid-template-columns: 1fr !important; gap: 8px !important; }
}
`;

const banner = {
  bannerHeading: "Our History",
  subheading:
    "From one classroom in Castle Hill in 2002 to more than thirty campuses around Australia.",
  path: [{ link: "/our-history", name: "Our History" }],
};

function OurHistory() {
  return (
    <div style={{ background: "#ffffff", fontFamily: FONT }}>
      <style>{responsiveCss}</style>
      <Header />
      <CommonBanner {...banner} />

      <main>
        <section style={s.intro}>
          <div style={s.container}>
            <div style={s.narrow}>
              <p style={s.lead}>
                CS Education began in 2002 when Testimony and Phoebe Kim opened a
                small coaching college in Castle Hill, preparing students for the
                OC and Selective School exams.
              </p>
              <p style={s.para}>
                From the first year we wrote and published our own lesson books
                rather than buying them in. That decision shaped everything that
                followed: if we write the material, we can change it when a
                cohort struggles with something, and we can keep what is taught
                in class and what is printed on the page moving together.
              </p>
              <p style={s.para}>
                Over the next two decades that single classroom grew into a group
                covering coaching, publishing, marking and online learning, and
                spread from New South Wales into Victoria, Queensland and South
                Australia. Today we teach students from Kindergarten through to
                the HSC.
              </p>

              <blockquote style={s.quote}>
                To offer an educational solution providing everything necessary to
                equip students with an education equal to the best in the world in
                the 21st century.
                <p style={s.quoteBy}>CS Education Group &mdash; our aim</p>
              </blockquote>
            </div>
          </div>
        </section>

        <section style={s.timelineWrap}>
          <div style={s.container}>
            <div style={s.narrow}>
              <h2 style={s.h2}>Milestones</h2>
              {milestones.map((m) => (
                <div key={m.year} className="cs-history-row" style={s.row}>
                  <div style={s.year}>{m.year}</div>
                  <div>
                    <h3 style={s.itemTitle}>{m.title}</h3>
                    <p style={s.itemBody}>{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={s.ctaBand}>
          <div style={s.container}>
            <div className="mq-sand" style={s.ctaBox}>
              <div>
                <p style={s.ctaTitle}>Twenty years of teaching, one year level at a time</p>
                <p style={s.ctaBody}>
                  See which programs run for your child this term.
                </p>
              </div>
              <Link className="mq-btn" to="/our-courses">
                Browse courses
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default OurHistory;
